import { Product } from "./types";
import { generateProductSlug } from "./seo-utils";

/**
 * Generate schema.org ItemList structured data for a product catalog page
 */
export function generateItemListSchema(products: Product[]): object {
  const baseUrl = "https://stickersby.ai";

  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: "StickersBy AI Product Catalog",
    numberOfItems: products.length,
    itemListElement: products.map((product, index) => {
      const slug = product.slug || generateProductSlug(product.name);
      return {
        "@type": "ListItem",
        position: index + 1,
        url: `${baseUrl}/products/${slug}`,
        name: product.name,
      };
    }),
  };
}

/**
 * Generate schema.org Product structured data for a product
 * @param product - The product to generate structured data for
 * @returns JSON-LD Product schema object
 */
export function generateProductSchema(product: Product): object {
  const slug = product.slug || generateProductSlug(product.name);
  const baseUrl = "https://stickersby.ai";

  // Calculate price range from variants
  const prices = product.variants.map(v => v.stripePrice);
  const lowestPrice = Math.min(...prices);
  const highestPrice = Math.max(...prices);

  // Check if all variants have the same price
  const hasUniformPricing = lowestPrice === highestPrice;

  return {
    "@context": "https://schema.org",
    "@type": "Product",
    name: product.name,
    description: product.description,
    image: product.thumbnail,
    brand: {
      "@type": "Brand",
      name: "StickersBy AI",
    },
    offers: hasUniformPricing
      ? {
          "@type": "Offer",
          price: lowestPrice.toFixed(2),
          priceCurrency: "USD",
          availability: "https://schema.org/InStock",
          url: `${baseUrl}/products/${slug}`,
        }
      : {
          "@type": "AggregateOffer",
          lowPrice: lowestPrice.toFixed(2),
          highPrice: highestPrice.toFixed(2),
          priceCurrency: "USD",
          availability: "https://schema.org/InStock",
          url: `${baseUrl}/products/${slug}`,
        },
    category: product.categories?.[0] || "Custom Products",
  };
}

/**
 * Generate schema.org Organization structured data for the homepage
 * @returns JSON-LD Organization schema object
 */
export function generateOrganizationSchema(): object {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "StickersBy AI",
    url: "https://stickersby.ai",
    logo: "https://stickersby.ai/logo.png",
    description: "Create custom products with AI - stickers, mugs, t-shirts, and more",
    sameAs: [
      // Add social media profiles here when available
      // "https://twitter.com/stickersbyai",
      // "https://facebook.com/stickersbyai",
    ],
  };
}

/**
 * Generate schema.org WebSite structured data with search action
 * @returns JSON-LD WebSite schema object
 */
export function generateWebSiteSchema(): object {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: "StickersBy AI",
    url: "https://stickersby.ai",
    potentialAction: {
      "@type": "SearchAction",
      target: {
        "@type": "EntryPoint",
        urlTemplate: "https://stickersby.ai/products?search={search_term_string}",
      },
      "query-input": "required name=search_term_string",
    },
  };
}
